import Grid from 'components/elements/Grid';
import InputGroup from 'components/groups/InputGroup';
import COLORS from 'constants/colors';
import FormFieldSet from 'exports/components/FormFieldSet';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import type ShippingAdviceModel from '../../models/ShippingAdvice';
import type ShippingAdviceType from '../../types/shippingAdvice';
import FormButton from '../FormButton';

import AccordionFormBody from '../AccordionFormBody';

export interface ShippingAdviceProps {
  shippingAdvice: ShippingAdviceModel;
  exportIncoterm: String;
  packingListSealNumber?: string;
}

const ShippingAdviceWrapper = styled.div`
  border-top: 1px solid ${COLORS.GRAY};
  padding: 0.625rem 0;
`;

const ShippingAdvice = ({
  shippingAdvice,
  exportIncoterm,
  packingListSealNumber,
}: ShippingAdviceProps) => {
  const {
    reset,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    control,
    watch,
  } = useForm({
    defaultValues: {
      eta: shippingAdvice.eta || '',
      etd: shippingAdvice.etd || '',
      container_number: shippingAdvice.container_number || '',
      date_of_arrival: shippingAdvice.date_of_arrival || '',
    },
  });
  const eta = watch('eta');
  const etd = watch('etd');
  const containerNumber = watch('container_number');

  const [canBeExported, setCanBeExported] = useState(
    shippingAdvice.can_be_exported === undefined ? true : shippingAdvice.can_be_exported
  );

  const attributesFilled = Boolean(
    eta && etd && containerNumber && packingListSealNumber
  );

  const onSaveClick = handleSubmit(async (formData: ShippingAdviceType) => {
    await shippingAdvice.save(formData);

    reset(formData);
  });

  const handleOnFinalizeClick = handleSubmit(async () => {
    await shippingAdvice.finalize();
  });

  const onExportClick = handleSubmit(async (formData: ShippingAdviceType) => {
    await shippingAdvice.sendOut(formData);
    setCanBeExported(
      shippingAdvice.can_be_exported === undefined ? true : shippingAdvice.can_be_exported
    );
    reset(formData);
  });

  const renderExportButton = () => {
    if (shippingAdvice.finalized) return null;

    return (
      <FormButton>
        <button
          disabled={!attributesFilled || !canBeExported || isSubmitting}
          type='button'
          onClick={onExportClick}
          className='button button--green'
        >
          {I18n.translate('buttons.export')}
        </button>
      </FormButton>
    );
  };

  return (
    <ShippingAdviceWrapper>
      <AccordionFormBody
        isFinalizable={shippingAdvice.isFinalizable}
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        finalized={shippingAdvice.finalized}
        onSubmitClick={onSaveClick}
        onFinalizeClick={handleOnFinalizeClick}
        downloadUrl={
          exportIncoterm == 'CIF' || exportIncoterm == 'FOB'
            ? shippingAdvice.downloadUrl
            : ''
        }
        extraSubmitButton={renderExportButton()}
      >
        <FormFieldSet disabled={shippingAdvice.finalized}>
          <Grid grid={16}>
            <Controller
              name='date_of_arrival'
              control={control}
              defaultValue={shippingAdvice.eta}
              render={({ value, onChange }) => (
                <InputGroup
                  id='date_of_arrival'
                  name='date_of_arrival'
                  type='date'
                  value={value}
                  onChange={onChange}
                  label={I18n.translate(
                    'exports.form.shipping_advice.date_of_arrival_label'
                  )}
                  placeholder={I18n.translate(
                    'exports.form.shipping_advice.etd_placeholder'
                  )}
                />
              )}
            />

            <Controller
              name='etd'
              control={control}
              defaultValue={shippingAdvice.etd}
              render={({ value, onChange }) => (
                <InputGroup
                  id='etd'
                  name='etd'
                  type='date'
                  value={value}
                  onChange={onChange}
                  label={I18n.translate('exports.form.shipping_advice.etd_label')}
                  placeholder={I18n.translate(
                    'exports.form.shipping_advice.etd_placeholder'
                  )}
                  disabled={!canBeExported}
                />
              )}
            />

            <Controller
              name='eta'
              control={control}
              defaultValue={shippingAdvice.eta}
              render={({ value, onChange }) => (
                <InputGroup
                  id='eta'
                  name='eta'
                  value={value}
                  onChange={onChange}
                  type='date'
                  label={I18n.translate('exports.form.shipping_advice.eta_label')}
                  placeholder={I18n.translate(
                    'exports.form.shipping_advice.eta_placeholder'
                  )}
                />
              )}
            />

            <Controller
              name='container_number'
              control={control}
              defaultValue={shippingAdvice.container_number}
              render={({ value, onChange }) => (
                <InputGroup
                  id='container_number'
                  name='container_number'
                  value={value}
                  onChange={onChange}
                  label={I18n.translate(
                    'exports.form.shipping_advice.container_number_label'
                  )}
                  placeholder={I18n.translate(
                    'exports.form.shipping_advice.container_number_placeholder'
                  )}
                  disabled={!canBeExported}
                />
              )}
            />

            <InputGroup
              id='seal_number'
              name='seal_number'
              label={I18n.translate('exports.form.packing_list.seal_number_label')}
              disabled={true}
              value={packingListSealNumber}
            />
          </Grid>
        </FormFieldSet>
      </AccordionFormBody>
    </ShippingAdviceWrapper>
  );
};

export default observer(ShippingAdvice);
